import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from 'lib/utils';
import { Icon, type IconName } from './icon';

const buttonVariants = cva(
  'cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-treez-green text-dark-green-text hover:bg-treez-green hover:opacity-90',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        'destructive-outline':
          'border border-red-500 bg-white text-red-500 hover:bg-red-500 hover:text-white transition-all duration-200',
        'primary-outline':
          'border border-green-400 bg-white text-green-400 hover:bg-green-400 hover:text-white transition-all duration-200',
        outline:
          'border border-gray-blue-20 bg-white hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-gray-blue-0 text-gray-blue-80 border border-gray-blue-20 hover:enabled:bg-gray-blue-3 hover:enabled:border-gray-blue-20 hover:enabled:text-gray-blue-80 focus-visible:outline-green-400 focus-visible:outline focus-visible:outline-[3px] disabled:bg-gray-blue-2 disabled:text-gray-blue-20',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        muted:
          'bg-gray-blue-5 text-gray-blue-80 hover:bg-gray-blue-10 transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-green-400',
        link: 'text-dark-green-link underline-offset-4 hover:underline',
        'treez-green':
          'bg-treez-green text-dark-green-text hover:bg-treez-green hover:opacity-90',
      },
      size: {
        default: 'h-10 px-4 py-2',
        sm: 'h-9 px-3',
        lg: 'h-12 px-8',
        md: 'h-11 px-2',
        icon: 'h-10 w-10',
        full: 'w-full px-4 py-2.5',
      },
      rounded: {
        none: 'rounded-none',
        sm: 'rounded-sm',
        base: 'rounded',
        md: 'rounded-md',
        lg: 'rounded-lg',
        full: 'rounded-full',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
      rounded: 'md',
    },
  }
);

/**
 * Button component props extending HTML button attributes with variant support
 *
 * @property {boolean} asChild - Render as child element using Radix Slot (default: false)
 * @property {IconName} iconStart - Icon name to display before button text
 * @property {IconName} iconEnd - Icon name to display after button text
 * @property {number} iconSize - Size of both icons in pixels (default: 16)
 * @property {string} iconClassName - CSS classes to apply to both icons
 *
 * Variant Options:
 * - variant: 'default' | 'destructive' | 'destructive-outline' | 'primary-outline' |
 *            'outline' | 'secondary' | 'ghost' | 'muted' | 'link' | 'treez-green'
 * - size: 'default' | 'sm' | 'lg' | 'md' | 'icon' | 'full'
 * - rounded: 'none' | 'sm' | 'base' | 'md' | 'lg' | 'full'
 */
export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  iconStart?: IconName;
  iconEnd?: IconName;
  iconSize?: number;
  iconClassName?: string;
}

/**
 * Flexible, accessible button component built with Radix UI and class-variance-authority
 *
 * VARIANTS:
 * - default: Treez brand green with dark text (main CTAs)
 * - destructive: Red background for destructive actions (delete, remove)
 * - destructive-outline: Red outline with white background (secondary destructive)
 * - primary-outline: Primary color outline (secondary actions)
 * - outline: Gray outline (neutral secondary actions)
 * - secondary: Light gray with border (standard secondary actions)
 * - ghost: Transparent, shows background on hover (tertiary actions, icon buttons)
 * - link: Underlined text link style (in-text navigation)
 * - treez-green: Treez brand green (Treez-specific CTAs)
 *
 * SIZES:
 * - default: h-10 px-4 py-2 (standard buttons)
 * - sm: h-9 px-3 (compact layouts, secondary actions)
 * - lg: h-11 px-8 (hero sections, prominent CTAs)
 * - icon: h-10 w-10 (square icon-only buttons)
 * - full: w-full px-4 py-2.5 (full-width buttons for mobile/forms)
 *
 * ROUNDED:
 * - none: 0px (sharp corners)
 * - sm: 2px (slight rounding)
 * - base: 4px (default rounding)
 * - md: 6px (standard, default)
 * - lg: 8px (softer appearance)
 * - full: 9999px (pill-shaped buttons)
 *
 * @example
 * // Basic button
 * <Button>Click me</Button>
 *
 * @example
 * // With variant and size
 * <Button variant="secondary" size="lg">Large Secondary</Button>
 *
 * @example
 * // With start icon
 * <Button iconStart="search" variant="secondary">Search</Button>
 *
 * @example
 * // With end icon
 * <Button iconEnd="arrow-right">Next</Button>
 *
 * @example
 * // Both icons with custom size
 * <Button iconStart="download" iconEnd="chevron-down" iconSize={20}>
 *   Download
 * </Button>
 *
 * @example
 * // Full width destructive button
 * <Button variant="destructive" size="full">Delete Account</Button>
 *
 * @example
 * // Pill-shaped button
 * <Button rounded="full" variant="treez-green">Get Started</Button>
 *
 * @example
 * // Icon-only button
 * <Button variant="ghost" size="icon" iconStart="settings" aria-label="Settings" />
 *
 * @example
 * // As a link (using asChild with react-router)
 * <Button asChild variant="link">
 *   <Link to="/dashboard">Go to Dashboard</Link>
 * </Button>
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      rounded,
      asChild = false,
      iconStart,
      iconEnd,
      iconSize = 16,
      iconClassName,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, rounded, className }))}
        ref={ref}
        {...props}
      >
        {iconStart && (
          <Icon
            name={iconStart}
            size={iconSize}
            className={iconClassName}
            aria-hidden="true"
          />
        )}
        {children}
        {iconEnd && (
          <Icon
            name={iconEnd}
            size={iconSize}
            className={iconClassName}
            aria-hidden="true"
          />
        )}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
